import type { SeasonDeepContent } from "./types";

export const summerDeepContent: SeasonDeepContent = {
  LIGHT: {
    intro: `Light Summer sits at the pale, cool end of the color wheel — the softest and most delicate of the three Summer subseasons. Undertone is cool and pink-based, never golden or peachy. Value runs light: skin, hair, and eyes cluster in the pale-to-medium range with little separation, which is why heavy or dark color tends to swallow this coloring rather than frame it. Chroma leans soft rather than fully muted, so the light summer color palette isn't as dusty as True or Soft Summer — it's gentle pastels with a touch of clarity, like powder blue, seafoam, and soft lavender, rather than flat grey-toned hues. Picture Light Summer as Light Spring's undertone flipped from warm to cool while staying just as gentle and low-contrast. Move toward more muting and depth and you land in True Summer; push the same paleness toward warmth and you're in Light Spring instead. Light Summer is the hinge between Summer's coolness and Spring's lightness — cool enough to glow in powder pink, delicate enough to be overwhelmed by anything too saturated or too dark.`,
    selfCheck: {
      skin: [
        "Fair to light skin with a cool pink or neutral cast, never golden or peachy",
        "Flushes to a soft rosy pink rather than orange or bronze",
        "Looks harsh or aged under stark black or heavy, warm makeup",
        "Brightens noticeably next to soft white compared to warm ivory",
      ],
      hair: [
        "Natural color in the light range: ash blonde, cool light brown, or mousy blonde",
        "Rarely naturally black, jet dark, or warm golden",
        "Childhood hair color was often lighter and cooler than current shade",
        "Looks best with soft, cool highlights rather than golden balayage",
      ],
      eyes: [
        "Light, soft eye colors: cool blue, grey, soft green, or light hazel",
        "Often flecked with grey or cool blue near the pupil",
        "Rarely deep brown or warm amber-brown",
        "Eyes look brighter next to cool pastels than next to warm, earthy tones",
      ],
      overall: [
        "Low to medium contrast between skin, hair, and eyes",
        "Silver jewelry flatters more than gold",
        "Pastel, cool-toned clothing reads as fresh and healthy, not washed out",
        "Bold, saturated, or very dark colors overpower rather than flatter",
      ],
    },
    avoidDetails: [
      { name: "Black", hex: "#000000", reason: "Too heavy and stark for Light Summer's low-contrast, delicate coloring — it hardens the face instead of softening it.", swapFor: "Silver Grey", swapHex: "#C0C0C0" },
      { name: "Dark Navy", hex: "#000080", reason: "Reads as dense and severe next to pale, cool skin, overpowering the natural softness.", swapFor: "Powder Blue", swapHex: "#B0E0E6" },
      { name: "Bright Orange", hex: "#FF6600", reason: "The strong warmth clashes with Light Summer's cool undertone and can make skin look ruddy.", swapFor: "Peach Pink", swapHex: "#FFDAB9" },
      { name: "Warm Camel", hex: "#C19A6B", reason: "Too golden and dense for this pale, cool palette; it drains rather than warms the complexion.", swapFor: "Light Taupe", swapHex: "#D8CFC4" },
      { name: "Golden Yellow", hex: "#FFDF00", reason: "A saturated warm yellow overwhelms Light Summer's soft, low-chroma coloring and looks jarring against pale skin.", swapFor: "Buttercream", swapHex: "#FFF1C1" },
      { name: "Rust", hex: "#B7410E", reason: "Too deep, warm, and earthy for a subseason built on pale cool pastels; it looks costumey rather than flattering.", swapFor: "Rose Quartz", swapHex: "#F7CAC9" },
    ],
    comparisons: [
      {
        vsLabel: "Light Spring",
        vsHref: "/color-palette/spring/light",
        rows: [
          { attribute: "Undertone", thisSeason: "Cool, pink-based", otherSeason: "Warm, peachy" },
          { attribute: "Depth", thisSeason: "Light, low contrast", otherSeason: "Light, low contrast" },
          { attribute: "Chroma", thisSeason: "Soft and muted", otherSeason: "Soft with warm clarity" },
          { attribute: "Best blue", thisSeason: "Sky Blue (cool)", otherSeason: "Light Aqua", thisHex: "#87CEEB", otherHex: "#B3E3E2" },
          { attribute: "Metals", thisSeason: "Silver", otherSeason: "Gold", thisHex: "#C0C0C0", otherHex: "#FFD700" },
        ],
      },
      {
        vsLabel: "True Summer",
        vsHref: "/color-palette/summer/true",
        rows: [
          { attribute: "Undertone", thisSeason: "Cool, soft pink", otherSeason: "Cool, balanced" },
          { attribute: "Depth", thisSeason: "Light, low contrast", otherSeason: "Medium, moderate contrast" },
          { attribute: "Chroma", thisSeason: "Soft pastel", otherSeason: "Muted, more saturated" },
          { attribute: "Best pink", thisSeason: "Powder Pink", otherSeason: "Dusty Rose", thisHex: "#FFD1DC", otherHex: "#D4A5A5" },
          { attribute: "Metals", thisSeason: "Bright silver", otherSeason: "Soft silver", thisHex: "#C0C0C0", otherHex: "#C0C0C0" },
        ],
      },
    ],
    capsule: [
      { item: "Wool coat", color: "Silver Grey", hex: "#C0C0C0" },
      { item: "Blazer", color: "Light Taupe", hex: "#D8CFC4" },
      { item: "Blouse", color: "Soft White", hex: "#F5F5F5" },
      { item: "Trousers", color: "Pale Ivory", hex: "#FFFFF0" },
      { item: "Sweater", color: "Seafoam", hex: "#B5EAD7" },
      { item: "Dress", color: "Misty Blue", hex: "#B4CFEC" },
      { item: "Cardigan", color: "Lilac", hex: "#C8A2C8" },
      { item: "Shoes", color: "Pale Ivory", hex: "#FFFFF0" },
      { item: "Bag", color: "Light Taupe", hex: "#D8CFC4" },
      { item: "Denim", color: "Pale Periwinkle", hex: "#CCCCFF" },
    ],
    celebrities: [
      {
        name: "Emily Blunt",
        why: "Fair, cool-toned skin and light, ash-based hair keep her coloring soft and low-contrast, a hallmark of Light Summer.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/45/Emily_Blunt_at_WWD_Style_Awards_2026-02.jpg/330px-Emily_Blunt_at_WWD_Style_Awards_2026-02.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Emily_Blunt",
      },
      {
        name: "Amanda Peet",
        why: "Light, cool-toned hair against fair skin with a soft pink cast reflects Light Summer's pale, gentle palette.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/11/Amanda_Peet_September_2014_%28cropped%29.jpg/330px-Amanda_Peet_September_2014_%28cropped%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Amanda_Peet",
      },
      {
        name: "Jennifer Garner",
        why: "Soft, cool-toned coloring with light-to-medium hair and low contrast places her within the delicate Light Summer range.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/3d/Jennifer_Garner_-_2024_%2854106107278%29_%28cropped%29.jpg/330px-Jennifer_Garner_-_2024_%2854106107278%29_%28cropped%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Jennifer_Garner",
      },
      {
        name: "Michelle Pfeiffer",
        why: "Pale, cool skin and light blonde hair with minimal warmth give her the soft, low-contrast look typical of Light Summer.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/19/Michelle_Pfeiffer_Ant-Man_%26_The_Wasp_premiere.jpg/330px-Michelle_Pfeiffer_Ant-Man_%26_The_Wasp_premiere.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Michelle_Pfeiffer",
      },
      {
        name: "Kirsten Dunst",
        why: "Fair, cool-toned skin and light, ash-blonde hair keep her coloring pale and gentle, in line with Light Summer.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d0/Kirsten_Dunst_Cannes_2016.jpg/330px-Kirsten_Dunst_Cannes_2016.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Kirsten_Dunst",
      },
      {
        name: "Sarah Jessica Parker",
        why: "Light, cool-toned hair and fair skin with soft contrast reflect the delicate, low-saturation look of Light Summer.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4a/Sarah_Jessica_Parker_2022.jpg/330px-Sarah_Jessica_Parker_2022.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Sarah_Jessica_Parker",
      },
    ],
    extraFaqs: [
      {
        question: "How is Light Summer different from Light Spring?",
        answer: "Both are pale and low-contrast, but the undertone flips: Light Summer is cool and pink-based, Light Spring is warm and peachy. Hold a cool pink fabric and a peach fabric near your face — Light Summers glow with the cool pink.",
      },
      {
        question: "How is Light Summer different from True Summer?",
        answer: "Light Summer is paler with lower contrast; True Summer sits deeper and more muted overall. If very pale pastels look right but slightly deeper dusty tones feel flat, you're likely Light Summer rather than True Summer.",
      },
      {
        question: "What jewelry metals suit Light Summer?",
        answer: "Bright, polished silver and white gold are most flattering. Avoid yellow gold and antiqued or brushed finishes, which read as warm and can make cool, light skin look dull. Delicate, thin pieces suit this subseason better than heavy statement jewelry.",
      },
      {
        question: "How can I confirm I'm a Light Summer?",
        answer: "Drape a cool pastel like powder blue or seafoam against your face, then try a warm, saturated color like rust. If the pastel makes your skin look fresher and the warm color makes you look ruddy or tired, that's a strong Light Summer signal.",
      },
    ],
    vibe: {
      skinTones: ["#F7E4DC", "#EFD2C6", "#E0BDAE"],
      hairTones: ["#C8B79E", "#A69F8F", "#D4C8B8", "#A8998A"],
      eyeColors: ["#A8C4D8", "#B8B8BE", "#9EC4B0", "#C9BFC4"],
    },
    hairToneSwatches: [
      { name: "Ash Blonde", hexes: ["#968976", "#C8B79E", "#D6C9B6"] },
      { name: "Light Ash Brown", hexes: ["#7C776B", "#A69F8F", "#BCB7AB"] },
      { name: "Cool Blonde", hexes: ["#9F968A", "#D4C8B8", "#DFD6CA"] },
      { name: "Soft Mushroom", hexes: ["#7E7368", "#A8998A", "#BEB2A7"] },
      { name: "Platinum Blonde", hexes: ["#AEABA9", "#E8E4E1", "#EEEBE8"] },
      { name: "Cool Light Brown", hexes: ["#74665A", "#9A8878", "#B3A69A"] },
      { name: "Soft Bronde", hexes: ["#8A7E6C", "#B8A890", "#CABEAC"] },
    ],
    metals: [
      { name: "Rose Gold", hexes: ["#AE878A", "#E8B4B8", "#EEC7CA"] },
      { name: "Soft Silver", hexes: ["#9F9F9F", "#D4D4D4", "#DFDFDF"] },
      { name: "White Gold", hexes: ["#AEABA9", "#E8E4E1", "#EEEBE8"] },
    ],
  },
  TRUE: {
    intro: `True Summer is the "quintessential" Summer — the subseason where coolness and softness are both at their most balanced. Undertone is unmistakably cool and blue-based, more obviously so than Light Summer's soft pink. Value sits in the light-to-medium range, deeper and richer than Light Summer but nowhere near Winter's depth. Chroma is muted rather than clear: colors read as gentle and blended, not dusty-flat or vivid, which is the main thing that separates the true summer color palette from its cool-and-vivid cousin, Cool Winter. Think of True Summer as sitting between Light Summer's pastel softness and Soft Summer's deeper muting — cool like Winter, but soft like Spring, with none of either season's extremes. Slate blue, mauve, and dusty rose all look natural rather than flat on True Summer. The tell is simple: if a color looks "calm" and "elegant" on you rather than "harsh" or "vivid," and it's cool rather than warm, you're likely in True Summer territory.`,
    selfCheck: {
      skin: [
        "Obvious cool pink or neutral undertone, medium depth",
        "Tans to a soft taupe rather than golden or bronze",
        "Looks sallow or flushed in warm, golden colors",
        "Comes alive next to muted, cool colors like slate blue or dusty rose",
      ],
      hair: [
        "Natural color has visible ash, cool brown, or smoky tones",
        "Medium depth — not as light as Light Summer, not as deep as Soft Summer often runs",
        "Highlights should be cool (ash, mushroom) rather than golden",
        "Looks brassy or off with warm, honey-toned browns",
      ],
      eyes: [
        "Cool grey, blue, soft green, or muted hazel",
        "Often has visible blue or grey flecks",
        "Eyes look most vivid next to muted cool colors, not warm ones",
        "Rarely a warm, golden brown",
      ],
      overall: [
        "Medium, blended contrast between skin, hair, and eyes",
        "Silver jewelry is clearly more flattering than gold",
        "Warm or highly saturated colors (rust, mustard) make the whole look harsh",
        "Muted, cool colors read as elegant rather than dull",
      ],
    },
    avoidDetails: [
      { name: "Bright Red", hex: "#FF0000", reason: "Too saturated and warm-leaning for True Summer's need for softness, making the look feel harsh rather than elegant.", swapFor: "Dusty Rose", swapHex: "#D4A5A5" },
      { name: "Warm Mustard", hex: "#FFDB58", reason: "The golden warmth clashes with True Summer's cool undertone and can make the complexion look sallow.", swapFor: "Sand", swapHex: "#C2B280" },
      { name: "Dark Chocolate", hex: "#381819", reason: "Too warm and dense for this cool, muted palette; it overwhelms rather than grounds the look.", swapFor: "Taupe", swapHex: "#483C32" },
      { name: "Pure Black", hex: "#000000", reason: "Too stark and high-contrast for True Summer's soft, blended coloring.", swapFor: "Slate Gray", swapHex: "#708090" },
      { name: "Orange", hex: "#FFA500", reason: "A fully warm, saturated hue that fights True Summer's cool, muted identity outright.", swapFor: "Rosewood", swapHex: "#B38481" },
      { name: "Bright Gold", hex: "#FFD700", reason: "Too shiny and warm for a subseason built on soft, cool neutrals; it looks disconnected from the skin.", swapFor: "Soft Grey", swapHex: "#B0B0B0" },
    ],
    comparisons: [
      {
        vsLabel: "Light Summer",
        vsHref: "/color-palette/summer/light",
        rows: [
          { attribute: "Undertone", thisSeason: "Cool, balanced", otherSeason: "Cool, soft pink" },
          { attribute: "Depth", thisSeason: "Medium, moderate contrast", otherSeason: "Light, low contrast" },
          { attribute: "Chroma", thisSeason: "Muted, more saturated", otherSeason: "Soft pastel" },
          { attribute: "Best pink", thisSeason: "Dusty Rose", otherSeason: "Powder Pink", thisHex: "#D4A5A5", otherHex: "#FFD1DC" },
          { attribute: "Metals", thisSeason: "Soft silver", otherSeason: "Bright silver", thisHex: "#C0C0C0", otherHex: "#C0C0C0" },
        ],
      },
      {
        vsLabel: "Soft Summer",
        vsHref: "/color-palette/summer/cool",
        rows: [
          { attribute: "Undertone", thisSeason: "Cool, balanced", otherSeason: "Cool, muted" },
          { attribute: "Depth", thisSeason: "Medium, moderate contrast", otherSeason: "Medium, low-to-moderate contrast" },
          { attribute: "Chroma", thisSeason: "Muted with some clarity", otherSeason: "Most muted of all Summers" },
          { attribute: "Best blue", thisSeason: "Slate Blue", otherSeason: "Muted Teal", thisHex: "#5D6D8E", otherHex: "#5B8A8A" },
          { attribute: "Metals", thisSeason: "Silver", otherSeason: "Pewter, brushed silver", thisHex: "#C0C0C0", otherHex: "#899499" },
        ],
      },
    ],
    capsule: [
      { item: "Wool coat", color: "Taupe", hex: "#483C32" },
      { item: "Blazer", color: "Soft Grey", hex: "#B0B0B0" },
      { item: "Blouse", color: "Soft White", hex: "#F8F8F8" },
      { item: "Trousers", color: "Misty Taupe", hex: "#CCC5B9" },
      { item: "Sweater", color: "Sage", hex: "#BCB88A" },
      { item: "Dress", color: "Dusty Rose", hex: "#D4A5A5" },
      { item: "Cardigan", color: "Mauve", hex: "#E0B0FF" },
      { item: "Shoes", color: "Dove Grey", hex: "#D5D5D5" },
      { item: "Bag", color: "Rosewood", hex: "#B38481" },
      { item: "Denim", color: "Slate Blue", hex: "#5D6D8E" },
    ],
    celebrities: [
      {
        name: "Meryl Streep",
        why: "Cool, soft coloring with ash-toned hair and muted contrast reflects True Summer's balanced, blended palette.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e5/Meryl_Streep-_Press_conference_for_the_film_%22The_Devil_Wears_Prada_2%22_-_55194765350_%28cropped1%29.jpg/330px-Meryl_Streep-_Press_conference_for_the_film_%22The_Devil_Wears_Prada_2%22_-_55194765350_%28cropped1%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Meryl_Streep",
      },
      {
        name: "Claire Danes",
        why: "Cool, fair skin with soft medium-depth hair and muted contrast place her within True Summer's calm, elegant range.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Claire_Danes_in_2015.jpg/330px-Claire_Danes_in_2015.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Claire_Danes",
      },
      {
        name: "Naomi Watts",
        why: "Cool undertone with soft, medium-depth coloring and blended contrast is a solid match for True Summer's balanced palette.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/55/Naomi_Watts_at_the_2024_New_York_Film_Festival_2_%28cropped%29.jpg/330px-Naomi_Watts_at_the_2024_New_York_Film_Festival_2_%28cropped%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Naomi_Watts",
      },
      {
        name: "Helen Mirren",
        why: "Cool, soft coloring with silvery hair and muted contrast reflect True Summer's gentle, understated elegance.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/bf/MKr24712_Helen_Mirren_%28Golda%2C_Berlinale_2023%29_cropped.png/330px-MKr24712_Helen_Mirren_%28Golda%2C_Berlinale_2023%29_cropped.png",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Helen_Mirren",
      },
      {
        name: "Miranda Kerr",
        why: "Cool, soft skin with medium-depth blonde hair and gentle, blended contrast place her within True Summer's balanced coloring.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/ca/Miranda_Kerr_-_Abbot_Kinney_2025.jpg/330px-Miranda_Kerr_-_Abbot_Kinney_2025.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Miranda_Kerr",
      },
      {
        name: "Dakota Johnson",
        why: "Cool undertone with soft, medium-brown hair and low-to-medium contrast make her a solid match for True Summer's calm, blended palette.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c2/Dakota_Johnson_at_the_2025_Cannes_Film_Festival_%28cropped%29.jpg/330px-Dakota_Johnson_at_the_2025_Cannes_Film_Festival_%28cropped%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Dakota_Johnson",
      },
    ],
    extraFaqs: [
      {
        question: "Can True Summer wear black?",
        answer: "Pure black is generally too stark and high-contrast for True Summer's soft, blended coloring — it can look severe rather than elegant. Slate gray or soft navy work better as your darkest neutral, especially away from the face.",
      },
      {
        question: "How is True Summer different from Light Summer?",
        answer: "True Summer has more depth and a more muted quality; Light Summer is paler with lower contrast. If dusty rose feels more natural than a very pale pastel, you're likely True Summer rather than Light Summer.",
      },
      {
        question: "What jewelry metals suit True Summer?",
        answer: "Soft, slightly brushed silver is the strongest choice, with platinum as a close second. Yellow gold tends to look disconnected from True Summer's cool undertone, making jewelry read as mismatched rather than polished.",
      },
      {
        question: "How can I confirm I'm a True Summer?",
        answer: "Compare a muted slate blue to a warm, saturated rust near your face. True Summers look calm and put-together in the slate blue but flushed or dull in the rust. If the cool, muted color wins clearly, True Summer is a strong match.",
      },
    ],
    vibe: {
      skinTones: ["#F0DCD2", "#E0C4B8", "#C9A696", "#AD8878"],
      hairTones: ["#6B5A4A", "#8A7B6B", "#5A4A3A", "#8B8070"],
      eyeColors: ["#8A97A0", "#6E8A8A", "#A0968A", "#7E8C99"],
    },
    hairToneSwatches: [
      { name: "Cool Brown", hexes: ["#504438", "#6B5A4A", "#908377"] },
      { name: "Ash Brown", hexes: ["#685C50", "#8A7B6B", "#A79C90"] },
      { name: "Soft Brunette", hexes: ["#44382C", "#5A4A3A", "#83776B"] },
      { name: "Mushroom Brown", hexes: ["#686054", "#8B8070", "#A8A094"] },
      { name: "Cool Dark Blonde", hexes: ["#7E7468", "#A89B8B", "#BEB4A8"] },
      { name: "Soft Black", hexes: ["#24211E", "#2C2824", "#6B6866"] },
      { name: "Muted Auburn", hexes: ["#5C382C", "#7B4A3A", "#9C776B"] },
    ],
    metals: [
      { name: "Silver", hexes: ["#909090", "#C0C0C0", "#D0D0D0"] },
      { name: "Rose Gold", hexes: ["#977878", "#C9A0A0", "#D6B8B8"] },
      { name: "White Gold", hexes: ["#A8A5A2", "#E0DCD8", "#E8E5E2"] },
    ],
  },
  COOL: {
    intro: `Soft Summer is the deepest and most muted of the three Summer subseasons — cool undertone, pushed toward the softest, most blended end of the chroma spectrum. Undertone stays cool, same family as Light and True Summer, but the palette leans more neutral-muted than clearly pink-based. Value sits medium, deeper than Light Summer and roughly on par with True Summer, while chroma is where Soft Summer stands apart — colors need to be dusty and greyed-down or they read as too intense. This is the soft summer color palette that borrows Autumn's muted quality while keeping Summer's coolness, which is why it's most often confused with Soft Autumn. The dividing line is undertone: Soft Summer wants cool teal and muted plum, while Soft Autumn wants warm moss and muted rust. Move up in clarity and contrast from Soft Summer and you land in True Summer; keep the muting but flip the undertone warm and you're in Soft Autumn instead. Bright, saturated colors are the fastest way to look overdone here — Soft Summer needs restraint, not voltage.`,
    selfCheck: {
      skin: [
        "Cool, neutral, or slightly muted undertone with medium depth",
        "Looks most polished next to dusty, blended color, not vivid tones",
        "Can appear flushed or harsh in bright, saturated brights",
        "Tans to a soft taupe-grey rather than golden or bronze",
      ],
      hair: [
        "Often medium depth with cool, ashy or smoky undertones: soft brown, cool dark blonde, or grey-brown",
        "Low-to-medium contrast against skin is typical",
        "Warm, golden, or brassy tones make the hair look off rather than rich",
        "Matte, soft finishes suit this hair better than high shine",
      ],
      eyes: [
        "Soft, muted eye color — grey-blue, muted green, or soft grey-hazel",
        "Low-to-medium contrast between iris and white of the eye",
        "Eyes look most balanced next to muted cool colors",
        "Bright, saturated eyeshadow shades tend to overpower rather than define the eyes",
      ],
      overall: [
        "Low-to-medium, blended contrast between skin, hair, and eyes",
        "Brushed or matte silver jewelry outperforms shiny gold or bright silver",
        "Muted, dusty cool colors look natural; bright or warm colors look costumey",
        "Soft neutrals and blended jewel tones read as flattering, not flat",
      ],
    },
    avoidDetails: [
      { name: "Bright Orange", hex: "#FF6600", reason: "Too warm and saturated for Soft Summer's muted, cool-neutral coloring, making it look overdone.", swapFor: "Muted Rose", swapHex: "#C76B8A" },
      { name: "Warm Coral", hex: "#FF7F50", reason: "The strong warmth clashes with Soft Summer's cool undertone and can make the skin look ruddy.", swapFor: "Raspberry", swapHex: "#9B3A62" },
      { name: "Warm Gold", hex: "#FFD700", reason: "Too shiny and warm for this soft, muted palette; it looks disconnected from the skin rather than luminous.", swapFor: "Pewter", swapHex: "#899499" },
      { name: "Rich Camel", hex: "#C19A6B", reason: "Too dense and golden for Soft Summer's cool, blended neutrals; it overwhelms rather than grounds the look.", swapFor: "Cool Slate", swapHex: "#5A6D7A" },
      { name: "Bright Fuchsia", hex: "#FF00FF", reason: "Too saturated and high-voltage for a subseason built on restraint; it reads as costumey rather than elegant.", swapFor: "Plum", swapHex: "#8E4585" },
      { name: "Golden Yellow", hex: "#FFDF00", reason: "A fully warm, vivid hue that fights Soft Summer's muted, cool identity outright.", swapFor: "Cool Plum", swapHex: "#6B4570" },
    ],
    comparisons: [
      {
        vsLabel: "Soft Autumn",
        vsHref: "/color-palette/autumn/soft",
        rows: [
          { attribute: "Undertone", thisSeason: "Cool, muted", otherSeason: "Warm, muted" },
          { attribute: "Depth", thisSeason: "Medium, low-to-moderate contrast", otherSeason: "Medium, low-to-moderate contrast" },
          { attribute: "Chroma", thisSeason: "Soft and blended", otherSeason: "Soft and blended" },
          { attribute: "Best green", thisSeason: "Cool Spruce", otherSeason: "Moss Green (warm)", thisHex: "#4A7C6F", otherHex: "#8A9A5B" },
          { attribute: "Metals", thisSeason: "Brushed silver, pewter", otherSeason: "Brushed gold, bronze", thisHex: "#899499", otherHex: "#8A5A3A" },
        ],
      },
      {
        vsLabel: "True Summer",
        vsHref: "/color-palette/summer/true",
        rows: [
          { attribute: "Undertone", thisSeason: "Cool, muted", otherSeason: "Cool, balanced" },
          { attribute: "Depth", thisSeason: "Medium, low-to-moderate contrast", otherSeason: "Medium, moderate contrast" },
          { attribute: "Chroma", thisSeason: "Most muted of all Summers", otherSeason: "Muted with some clarity" },
          { attribute: "Best blue", thisSeason: "Muted Teal", otherSeason: "Slate Blue", thisHex: "#5B8A8A", otherHex: "#5D6D8E" },
          { attribute: "Metals", thisSeason: "Pewter, brushed silver", otherSeason: "Silver", thisHex: "#899499", otherHex: "#C0C0C0" },
        ],
      },
    ],
    capsule: [
      { item: "Wool coat", color: "Pewter", hex: "#899499" },
      { item: "Blazer", color: "Soft Navy", hex: "#4C516D" },
      { item: "Blouse", color: "Frosted White", hex: "#F0F0F0" },
      { item: "Trousers", color: "Cool Slate", hex: "#5A6D7A" },
      { item: "Sweater", color: "Muted Teal", hex: "#5B8A8A" },
      { item: "Dress", color: "Plum", hex: "#8E4585" },
      { item: "Cardigan", color: "Muted Rose", hex: "#C76B8A" },
      { item: "Shoes", color: "Silver", hex: "#C0C0C0" },
      { item: "Bag", color: "Steel Gray", hex: "#71797E" },
      { item: "Denim", color: "Medium Navy", hex: "#3B5998" },
    ],
    celebrities: [
      {
        name: "Cate Blanchett",
        why: "Cool, muted coloring with soft contrast between skin and hair gives her the blended, understated quality central to Soft Summer.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Cate_Blanchett-63298_%28cropped_2%29.jpg/330px-Cate_Blanchett-63298_%28cropped_2%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Cate_Blanchett",
      },
      {
        name: "Gwyneth Paltrow",
        why: "Cool undertone with soft, medium-depth coloring and low contrast align with Soft Summer's restrained, elegant look.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c5/Gwyneth_Paltrow_-_Marty_Supreme.jpg/330px-Gwyneth_Paltrow_-_Marty_Supreme.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Gwyneth_Paltrow",
      },
      {
        name: "Taylor Swift",
        why: "Cool, fair skin with soft, blended contrast against her natural coloring places her within Soft Summer's muted range.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b1/Taylor_Swift_at_the_2023_MTV_Video_Music_Awards_%283%29.png/330px-Taylor_Swift_at_the_2023_MTV_Video_Music_Awards_%283%29.png",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Taylor_Swift",
      },
      {
        name: "Diane Kruger",
        why: "Cool, soft coloring with medium-depth blonde hair and gentle contrast fit comfortably within Soft Summer's blended palette.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/7b/MKr378435_Diane_Kruger_%28Amrum%2C_Cannes_2025%29.jpg/330px-MKr378435_Diane_Kruger_%28Amrum%2C_Cannes_2025%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Diane_Kruger",
      },
      {
        name: "Michelle Williams",
        why: "Cool, muted coloring with soft, low-contrast features reflects the gentle, blended quality that defines Soft Summer.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/98/MichelleWilliams-byPhilipRomano.jpg/330px-MichelleWilliams-byPhilipRomano.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Michelle_Williams_(actress)",
      },
      {
        name: "Saoirse Ronan",
        why: "Cool, fair skin with soft, muted contrast against her natural coloring is a strong match for Soft Summer's restrained palette.",
        imageUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/ae/MKr349648_Saoirse_Ronan_%28The_Outrun%2C_Berlinale_2024%29.jpg/330px-MKr349648_Saoirse_Ronan_%28The_Outrun%2C_Berlinale_2024%29.jpg",
        wikipediaUrl: "https://en.wikipedia.org/wiki/Saoirse_Ronan",
      },
    ],
    extraFaqs: [
      {
        question: "Can Soft Summer wear black?",
        answer: "Pure black is usually too stark and high-contrast for Soft Summer's muted, blended coloring — it can look severe rather than soft. Soft navy or steel gray work better as your darkest neutral, especially kept away from the face.",
      },
      {
        question: "How is Soft Summer different from Soft Autumn?",
        answer: "Both are muted and blended, but the undertone flips: Soft Summer is cool (teal, plum, raspberry), Soft Autumn is warm (moss, rust, camel). Hold a cool muted teal and a warm muted olive near your face — Soft Summers look fresher in the teal.",
      },
      {
        question: "How is Soft Summer different from True Summer?",
        answer: "Soft Summer is even more muted and blended than True Summer, which carries a touch more clarity. If very soft, dusty tones feel right but slightly clearer cool colors feel like too much, you're likely Soft Summer.",
      },
      {
        question: "What jewelry metals suit Soft Summer?",
        answer: "Brushed or matte silver and pewter finishes work best — the softness matches this subseason's need for restraint. Avoid shiny yellow gold or polished bright silver, both of which can look too sharp against Soft Summer's muted coloring.",
      },
    ],
    vibe: {
      skinTones: ["#F4E0DA", "#E5C9C4", "#CFA9A6", "#B08D8C"],
      hairTones: ["#B8A898", "#6A5A4A", "#E0D8D0", "#9A908A"],
      eyeColors: ["#6E8FA8", "#8A96A0", "#A0A8A0", "#7C8890"],
    },
    hairToneSwatches: [
      { name: "Ash Blonde", hexes: ["#8A7E72", "#B8A898", "#CABEB2"] },
      { name: "Cool Brown", hexes: ["#504438", "#6A5A4A", "#8F8377"] },
      { name: "Icy Blonde", hexes: ["#A8A29C", "#E0D8D0", "#E8E2DC"] },
      { name: "Soft Ash", hexes: ["#746C68", "#9A908A", "#B3ACA7"] },
      { name: "Platinum", hexes: ["#AEABA9", "#E8E4E1", "#EEEBE8"] },
      { name: "Cool Black", hexes: ["#171717", "#1C1C1C", "#606060"] },
      { name: "Silver Grey", hexes: ["#7E7E7E", "#A8A8A8", "#BEBEBE"] },
    ],
    metals: [
      { name: "Silver", hexes: ["#909090", "#C0C0C0", "#D0D0D0"] },
      { name: "Platinum", hexes: ["#ACABAA", "#E5E4E2", "#ECEBE9"] },
      { name: "White Gold", hexes: ["#AEABA9", "#E8E4E1", "#EEEBE8"] },
    ],
  },
};
